import {Component, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {
    CardDto,
    CardService,
    CardState,
    PageableRequestDtoOfRfidCardCriteriaDto,
    PagedResultDtoOfCardDto,
    RfidCardCriteriaDto,
    RfidCardSearchCriteriaDto,
    SearchCustomerDto,
    SortCriteriaDto,
    SortDirection
} from '../_services/configuration-services';
import {NotificationsService} from '../_shared/notifications.service';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {RfidCardModalComponent} from '../_shared/_components/rfid-card-modal/rfid-card-modal.component';
import {faPen, faTrash, faUserPen} from '@fortawesome/free-solid-svg-icons';
import {ConfirmModalService} from '../_shared/_components/confirm-modal/confirm-modal.component';
import {IDropdownSettings} from "ng-multiselect-dropdown";
import {TranslateService} from "@ngx-translate/core";
import {UntypedFormControl, UntypedFormGroup} from "@angular/forms";
import {firstValueFrom, Subscription} from "rxjs";
import {PermissionKeys} from '../app.constants';

@Component({
    selector: 'app-cards',
    templateUrl: './cards.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CardsComponent implements OnInit, OnDestroy {

    // Icons
    faTrash = faTrash;
    faPen = faPen;
    faUserPen = faUserPen;

    // Permissions
    protected readonly PermissionKeys = PermissionKeys;

    // Import
    rfidCardsFileToUpload: File;
    rfidCardsFileValid: boolean;

    // DataTable
    readonly pageSize = 50;
    crtPage = 0;
    rfidCards: PagedResultDtoOfCardDto = new PagedResultDtoOfCardDto();

    rfidCardStatusSettings: IDropdownSettings = {};
    rfidCardStatusSource = [];

    private currentSort = new SortCriteriaDto({
        direction: SortDirection.Asc,
        property: 'cardNumber'
    });

    pageRequest: PageableRequestDtoOfRfidCardCriteriaDto;

    rfidFormGroup = new UntypedFormGroup({
        cardState: new UntypedFormControl(),
        cardUid: new UntypedFormControl(),
        cardNumber: new UntypedFormControl(),
        customer: new UntypedFormControl(),
    });

    private langChangeSubscription$: Subscription;

    constructor(private readonly rfidCardService: CardService,
                private readonly notificationService: NotificationsService,
                private readonly confirmService: ConfirmModalService,
                private readonly translateService: TranslateService,
                private readonly modalService: NgbModal) {

        this.pageRequest = new PageableRequestDtoOfRfidCardCriteriaDto({
            page: this.crtPage + 1,
            pageSize: this.pageSize
        });
        this.pageRequest.criteria = new RfidCardCriteriaDto({
            rfidCardSearchCriteriaDto: new RfidCardSearchCriteriaDto({
                cardNumber: null,
                uid: null,
                assigned: null
            }),
            sortCriteriaDto: this.currentSort
        });
    }

    ngOnInit(): void {
        this.langChangeSubscription$ = this.translateService.onLangChange.subscribe(_ => {
            this.populateRfidStatusSelect();
        });
        this.populateRfidStatusSelect();
        void this.fetchRfidCards();
    }

    ngOnDestroy(): void {
        this.langChangeSubscription$.unsubscribe();
        this.modalService.dismissAll('destroy');
    }

    rfidCardsFileSelected(files: any): void {
        if (files.length > 0) {
            this.rfidCardsFileToUpload = files[0];
            this.rfidCardsFileValid = true;
        }
    }

    create(): void {
        this.modalService.open(RfidCardModalComponent, {centered: true}).result
            .then(async value => {
                if (value === 'success') {
                    this.notificationService.success({title: 'cards.notifications.createSuccess'});
                    await this.fetchRfidCards();
                }
            }, () => { /* catch the rejection */ });
    }

    update(rfidCard: CardDto): void {
        const modal = this.modalService.open(RfidCardModalComponent, {centered: true});
        modal.componentInstance.rfidCard = rfidCard;
        modal.result
            .then(async value => {
                if (value === 'success') {
                    this.notificationService.success({title: 'cards.notifications.updateSuccess'});
                    await this.fetchRfidCards();
                }
            }, () => { /* catch the rejection */ });
    }

    delete(rfidCard: CardDto): void {
        this.confirmService.confirm({titleKey: 'common.confirmModal.title.delete'}).then(result => {
            if (result === ConfirmModalService.yes) {
                this.rfidCardService.deleteRfidCard(rfidCard.cardNumber).toPromise().then(async () => {
                    this.notificationService.success({title: 'cards.notifications.deleteSuccess'});
                    await this.fetchRfidCards();
                });
            }
        });
    }

    // region DataTableMethods
    async setPage(page: any): Promise<void> {
        this.crtPage = page.offset;
        this.pageRequest.page = this.crtPage + 1;
        await this.fetchRfidCards();
    }

    async onSort(sortOrder: any): Promise<void> {
        this.goToFirstPage();
        this.currentSort = new SortCriteriaDto({
            direction: sortOrder.sorts[0].dir,
            property: sortOrder.sorts[0].prop
        });
        this.pageRequest.criteria.sortCriteriaDto = this.currentSort;
        await this.fetchRfidCards();
    }

    private goToFirstPage(): void {
        this.crtPage = 0;
        this.pageRequest.page = this.crtPage + 1;
    }

    async searchCriteriaChanged(): Promise<void> {
        this.goToFirstPage();
        await this.fetchRfidCards();
    }

    async fetchRfidCards(): Promise<void> {
        this.pageRequest.criteria.rfidCardSearchCriteriaDto.cardNumber = this.cardNumber.value;
        this.pageRequest.criteria.rfidCardSearchCriteriaDto.uid = this.cardUid.value;
        this.pageRequest.criteria.rfidCardSearchCriteriaDto.assigned = this.cardState.value?.map(item => item.itemId);
        this.pageRequest.criteria.rfidCardSearchCriteriaDto.customerId = this.customer.value;
        this.rfidCards = await firstValueFrom(this.rfidCardService.searchRfidCards(this.pageRequest));
    }

    async clearFilter(): Promise<void> {
        this.pageRequest.page = this.crtPage + 1;
        this.pageRequest.pageSize = this.pageSize;
        this.pageRequest.criteria = new RfidCardCriteriaDto({
            rfidCardSearchCriteriaDto: new RfidCardSearchCriteriaDto({
                cardNumber: null,
                uid: null,
                assigned: null
            }),
            sortCriteriaDto: this.currentSort
        });

        this.rfidFormGroup.reset();

        this.goToFirstPage();
        await this.fetchRfidCards();
    }

    isFiltering(): boolean {
        const rfidCardSearchCriteriaDto = this.pageRequest.criteria.rfidCardSearchCriteriaDto;
        return !!rfidCardSearchCriteriaDto.cardNumber ||
            !!rfidCardSearchCriteriaDto.uid ||
            rfidCardSearchCriteriaDto.assigned !== undefined;
    }

    // end region

    importRfidCardsFile(): void {
        this.rfidCardService.importRfidCardsFromFile({
            data: this.rfidCardsFileToUpload,
            fileName: this.rfidCardsFileToUpload.name
        }).toPromise()
            .then(nbRowsAdded => {
                this.notificationService.success({
                    message: 'cards.fullyImported',
                    interpolateParams: {ImportedRfidCardsCount: nbRowsAdded}
                });
                this.fetchRfidCards();
            });
    }

    private populateRfidStatusSelect() {
        this.rfidCardStatusSource = [];
        Object.keys(CardState)
            .forEach(r => {
                this.rfidCardStatusSource.push({
                    itemId: r,
                    itemVal: this.translateService.instant('cards.status.' + r.toLowerCase())
                })
            });
        this.rfidCardStatusSettings = {
            idField: 'itemId',
            textField: 'itemVal',
            singleSelection: false,
            selectAllText: this.translateService.instant('common.multiselect.selectAll'),
            unSelectAllText: this.translateService.instant('common.multiselect.unselectAll'),
            searchPlaceholderText: this.translateService.instant('common.multiselect.search'),
            noDataAvailablePlaceholderText: this.translateService.instant('common.multiselect.noResults'),
            itemsShowLimit: 3,
            allowSearchFilter: false
        };
    }


    async customerSelected($event: SearchCustomerDto): Promise<void> {
        this.customer.setValue($event?.id);
        await this.searchCriteriaChanged();
    }

    isVirtualCard(card: CardDto) : boolean {
        return card.cardState === CardState.Virtual;
    }

    get cardState(): UntypedFormControl {
        return this.rfidFormGroup.get('cardState') as UntypedFormControl;
    }

    get cardUid(): UntypedFormControl {
        return this.rfidFormGroup.get('cardUid') as UntypedFormControl;
    }

    get cardNumber(): UntypedFormControl {
        return this.rfidFormGroup.get('cardNumber') as UntypedFormControl;
    }

    get customer(): UntypedFormControl {
        return this.rfidFormGroup.get('customer') as UntypedFormControl;
    }
}
